import type { UseFormReturn } from "react-hook-form";
import { t } from "ttag";

import type { BmiForm } from "@/shared/models";
import { getUnitSystemOptions, UNIT_SYSTEM } from "@/shared/models";
import { Card, Form, RadioGroup } from "@/shared/ui";
import {
  convertImperialToMetricBmiInputs,
  convertMetricToImperialBmiInputs,
} from "@/shared/utils";

interface UnitSystemSelectorProps {
  form: UseFormReturn<BmiForm>;
}

export const UnitSystemSelector = ({ form }: UnitSystemSelectorProps) => {
  return (
    <Card.Root className="rounded-lg">
      <Form.Field
        control={form.control}
        name="unitSystem"
        render={({ field: { onChange, ...field } }) => (
          <Form.Item className="grow flex-row justify-between">
            <Form.Label className="text-base font-medium text-neutral-950">
              {t`Unit system`}
            </Form.Label>

            <Form.Control>
              <RadioGroup.Root
                className="flex items-center gap-6"
                onValueChange={(value: BmiForm["unitSystem"]) => {
                  onChange(value);
                  if (value === UNIT_SYSTEM.METRIC) {
                    const { heightInCentimeters, weightInKilograms } =
                      convertImperialToMetricBmiInputs({
                        heightFeet: form.getValues("heightFeet"),
                        heightInches: form.getValues("heightInches"),
                        weightInPounds: form.getValues("weightInPounds"),
                      });

                    form.setValue("heightInCentimeters", heightInCentimeters);
                    form.setValue("weightInKilograms", weightInKilograms);
                  }
                  if (value === UNIT_SYSTEM.IMPERIAL) {
                    const { heightFeet, heightInches, weightInPounds } =
                      convertMetricToImperialBmiInputs({
                        heightInCentimeters: form.getValues(
                          "heightInCentimeters",
                        ),
                        weightInKilograms: form.getValues("weightInKilograms"),
                      });

                    form.setValue("heightFeet", heightFeet);
                    form.setValue("heightInches", heightInches);
                    form.setValue("weightInPounds", weightInPounds);
                  }
                }}
                {...field}
              >
                {getUnitSystemOptions().map((option) => (
                  <RadioGroup.Item key={option.value} value={option.value}>
                    <RadioGroup.Label>
                      {option.label}{" "}
                      <span className="text-neutral-700">({option.unit})</span>
                    </RadioGroup.Label>
                  </RadioGroup.Item>
                ))}
              </RadioGroup.Root>
            </Form.Control>
          </Form.Item>
        )}
      />
    </Card.Root>
  );
};
